import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, Users, Car, Euro, Building2, FileText, Download,
  RefreshCw, LogOut, ChevronRight, Search, Filter, 
  Calendar, Clock, MapPin, CheckCircle, XCircle, Loader2,
  TrendingUp, UserCheck, CreditCard, Eye,
  MessageCircle, Phone, CalendarDays, Trash2, Pencil, ChevronLeft, X, Award,
  DollarSign, Ban, Lock, Navigation, Bell, BellOff, Plus, Calculator, RotateCcw, IdCard, Shield
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AdminNotifications } from '../components/AdminNotifications';
import { AvailabilityManager } from '../components/admin/AvailabilityManager';
import { DriversTab } from '../components/admin/DriversTab';
import { InvoicesTab } from '../components/admin/InvoicesTab';
import { ExportTab } from '../components/admin/ExportTab';
import { PaymentModal } from '../components/admin/PaymentModal';
import { DriverLinkModal } from '../components/admin/DriverLinkModal';
import { RideWorkflowModal } from '../components/admin/RideWorkflowModal';
import { SecurityTab } from '../components/admin/SecurityTab';
import { PushNotificationButton } from '../components/PushNotificationButton';
import { GooglePlacesAutocomplete } from '../components/GoogleMaps';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// IATA flight number heuristic: 2 chars (airline) + 1-4 digits + optional suffix letter
// Examples: AF1234, LH456, U24587, BA001A, 6X123
const IATA_FLIGHT_RE = /^[A-Z0-9]{2}[A-Z]?\d{1,4}[A-Z]?$/;
const isValidFlightFormat = (fn) => !fn || IATA_FLIGHT_RE.test(fn.toUpperCase().trim());

export const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bookings');

  // Admin PWA manifest swap
  useEffect(() => {
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#10b981');
    return () => {};
  }, []);
  
  // Data states
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [quickBookings, setQuickBookings] = useState([]);
  const [editingQuickBooking, setEditingQuickBooking] = useState(null);
  const [returnForm, setReturnForm] = useState(null); // {date, time, price, driver_id, driver_net_price, flight_number}
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentModal, setPaymentModal] = useState(null); // {type: 'capture'|'cancel', booking: ...}
  const [captureAmount, setCaptureAmount] = useState('');
  const [cancelFeePercent, setCancelFeePercent] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [driverLinkModal, setDriverLinkModal] = useState(null);
  const [driverLink, setDriverLink] = useState('');
  const [walletAmount, setWalletAmount] = useState('');
  const [walletReason, setWalletReason] = useState('Prime parrainage');
  const [walletLoading, setWalletLoading] = useState(false);
  const [privilegedClients, setPrivilegedClients] = useState([]);
  // Map "+33...": {is_privileged, source, ride_count_6m} — for badge in bookings list
  const [privilegeMap, setPrivilegeMap] = useState({});
  const [showAddPrivileged, setShowAddPrivileged] = useState(false);
  const [newPrivileged, setNewPrivileged] = useState({ phone: '', first_name: '', last_name: '', fixed_price: '', route_description: '', pickup_keywords: '', dest_keywords: '', notes: '' });
  const [drivers, setDrivers] = useState([]);
  const [showAddDriver, setShowAddDriver] = useState(false);
  const [newDriver, setNewDriver] = useState({ first_name: '', last_name: '', license_plate: '', phone: '', notes: '', commission_percent: 70, company_name: '', company_legal_form: '', company_siren: '', company_siret: '', company_ape: '', company_address: '', company_vat: '', evtc_card_number: '' });
  const [editingDriver, setEditingDriver] = useState(null);
  const [rideModal, setRideModal] = useState(null);
  const [rideLoading, setRideLoading] = useState(false);
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [newBooking, setNewBooking] = useState({
    first_name: '', last_name: '', phone: '', email: '',
    pickup_address: '', destination_address: '',
    pickup_lat: null, pickup_lng: null, dest_lat: null, dest_lng: null,
    date: '', time: '', booking_date: '', booking_time: '',
    total_price: '', payment_method: 'cash',
    stops: [], notes: '', driver_id: '', driver_net_price: '',
    show_price_to_client: true,
    is_professional: false,
    company_name: '', company_address: '', company_nif: '', company_vat: '',
    flight_number: '',
    // Round trip (Aller-Retour) — generates a second booking with addresses inverted
    is_round_trip: false,
    return_date: '', return_time: '',
    return_price: '', return_driver_id: '', return_driver_net_price: '',
    return_flight_number: ''
  });
  
  // Filters
  const [userFilter, setUserFilter] = useState({ businessOnly: false, search: '' });
  const [bookingFilter, setBookingFilter] = useState({ status: '', userId: '' });
  
  // Pagination
  const [usersTotal, setUsersTotal] = useState(0);
  const [bookingsTotal, setBookingsTotal] = useState(0);
  
  // Invoice generation
  const [generatingInvoices, setGeneratingInvoices] = useState(false);
  const [invoiceMonth, setInvoiceMonth] = useState(new Date().getMonth() + 1);
  const [invoiceYear, setInvoiceYear] = useState(new Date().getFullYear());
  
  // Push notification state
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  // Prevent multiple handleAuthError calls from parallel API failures
  const authErrorHandled = useRef(false);

  // Read admin token from localStorage OR cookie (iOS PWA fallback)
  const getAdminToken = useCallback(() => {
    let token = localStorage.getItem('admin_token');
    if (token) return token;
    // Fallback: read from cookie
    const match = document.cookie.match(/(?:^|;\s*)admin_token=([^;]+)/);
    if (match) {
      token = decodeURIComponent(match[1]);
      // Restore to localStorage
      localStorage.setItem('admin_token', token);
      return token;
    }
    return null;
  }, []);

  const getAuthHeader = useCallback(() => ({
    Authorization: `Bearer ${getAdminToken()}`
  }), [getAdminToken]);

  const handleAuthError = useCallback(() => {
    // Only handle auth error ONCE per session — prevents cascade from parallel 401s
    if (authErrorHandled.current) return;
    authErrorHandled.current = true;
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_last_login');
    document.cookie = 'admin_token=; path=/; max-age=0; SameSite=Lax; Secure';
    navigate('/admin/login');
    toast.error('Session expirée, veuillez vous reconnecter');
  }, [navigate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: getAuthHeader() });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
      }
    }
  }, [handleAuthError]);

  const fetchQuickBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/quick-bookings`, { headers: getAuthHeader() });
      setQuickBookings(res.data);
    } catch (error) {
      if (error.response?.status === 401) handleAuthError();
    }
  }, [handleAuthError]);

  const updateQuickBookingStatus = async (bookingId, status) => {
    try {
      const res = await axios.put(`${API}/admin/quick-bookings/${bookingId}/status?status=${status}`, null, { headers: getAuthHeader() });
      toast.success(res.data.message || `Reservation ${status}`);
      fetchQuickBookings();
    } catch (e) { toast.error('Erreur'); }
  };

  const deleteQuickBooking = async (bookingId, name) => {
    if (!window.confirm(`Supprimer la reservation de ${name} ?\nCette action est irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/quick-bookings/${bookingId}`, { headers: getAuthHeader() });
      toast.success('Reservation supprimee');
      fetchQuickBookings();
    } catch (e) { toast.error('Erreur lors de la suppression'); }
  };

  const saveQuickBookingEdit = async () => {
    if (!editingQuickBooking) return;
    try {
      const bid = editingQuickBooking.booking_id || editingQuickBooking.id || editingQuickBooking._id;
      // Only send non-empty fields to avoid overwriting with empty values
      const raw = {
        first_name: editingQuickBooking.first_name,
        last_name: editingQuickBooking.last_name,
        phone: editingQuickBooking.phone,
        email: editingQuickBooking.email,
        pickup_address: editingQuickBooking.pickup_address,
        destination_address: editingQuickBooking.destination_address,
        pickup_lat: editingQuickBooking.pickup_lat,
        pickup_lng: editingQuickBooking.pickup_lng,
        dest_lat: editingQuickBooking.dest_lat,
        dest_lng: editingQuickBooking.dest_lng,
        date: editingQuickBooking.date,
        time: editingQuickBooking.time,
        total_price: editingQuickBooking.total_price,
        flight_number: editingQuickBooking.flight_number,
      };
      // Filter out null/undefined/empty values
      // Note: flight_number and stops are handled separately to allow clearing them.
      const flightNumberRaw = editingQuickBooking.flight_number;
      const stopsRaw = editingQuickBooking.stops;
      const payload = {};
      for (const [k, v] of Object.entries(raw)) {
        if (k === 'flight_number') continue; // handled below
        if (v !== null && v !== undefined && v !== '') payload[k] = v;
      }
      // Always send flight_number so it can be cleared (empty string allowed)
      if (flightNumberRaw !== undefined) {
        payload.flight_number = (flightNumberRaw || '').toString().toUpperCase().trim();
      }
      // Always send stops (array) so additions/removals are persisted, including emptying the list
      if (Array.isArray(stopsRaw)) {
        payload.stops = stopsRaw
          .filter(s => s && s.address)
          .map(s => ({
            address: s.address,
            lat: s.lat ?? null,
            lng: s.lng ?? null,
            wait_minutes: parseInt(s.wait_minutes) || 0,
          }));
      }
      if (Object.keys(payload).length === 0) {
        toast.error('Aucune modification');
        return;
      }
      await axios.put(`${API}/admin/quick-bookings/${bid}`, payload, { headers: getAuthHeader() });
      toast.success('Reservation mise a jour');
      setEditingQuickBooking(null);
      fetchQuickBookings();
      fetchBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de la modification'); }
  };

  const recalculatePrice = async () => {
    if (!editingQuickBooking) return;
    const bid = editingQuickBooking.booking_id || editingQuickBooking.id;
    try {
      // First save current addresses
      await saveQuickBookingEdit();
      // Then recalculate
      const res = await axios.post(`${API}/admin/quick-bookings/${bid}/recalculate`, null, { headers: getAuthHeader() });
      toast.success(`Prix recalcule: ${res.data.new_price}€ (${res.data.distance_km}km, ${Math.round(res.data.duration_min)}min)`);
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de recalcul');
    }
  };

  const advanceRideStep = async (step) => {
    if (!rideModal) return;
    setRideLoading(true);
    const bid = rideModal.booking_id || rideModal.id;
    try {
      const res = await axios.post(`${API}/admin/quick-bookings/${bid}/ride-step?step=${step}`, null, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setRideModal({ ...rideModal, ride: res.data.ride });
      fetchQuickBookings();
      // On "en_route", open WhatsApp to share position
      if (step === 'en_route') {
        const text = `YOUMI GREEN VTC - Votre chauffeur est en route vers ${rideModal.pickup_address || 'votre adresse'}`;
        window.open(`https://wa.me/${(rideModal.phone || '').replace(/^0/, '33').replace(/[^0-9]/g, '')}?text=${encodeURIComponent(text)}`, '_blank');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setRideLoading(false); }
  };

  const createAdminBooking = async () => {
    try {
      // Build outbound payload (strip return-only fields)
      const { is_round_trip, return_date, return_time, return_price, return_driver_id, return_driver_net_price, return_flight_number, ...outbound } = newBooking;

      const res = await axios.post(`${API}/admin/create-booking`, outbound, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Course aller creee');

      // If round-trip, create the return booking (addresses inverted, return date/time/price/driver)
      if (is_round_trip && return_date && return_time && return_price) {
        const returnPayload = {
          ...outbound,
          pickup_address: outbound.destination_address,
          destination_address: outbound.pickup_address,
          pickup_lat: outbound.dest_lat,
          pickup_lng: outbound.dest_lng,
          dest_lat: outbound.pickup_lat,
          dest_lng: outbound.pickup_lng,
          date: return_date,
          time: return_time,
          total_price: return_price,
          driver_id: return_driver_id || outbound.driver_id,
          driver_net_price: return_driver_net_price || '',
          flight_number: return_flight_number || '',
          stops: [],
          notes: (outbound.notes ? outbound.notes + ' | ' : '') + 'Retour de la course aller',
        };
        try {
          await axios.post(`${API}/admin/create-booking`, returnPayload, { headers: getAuthHeader() });
          toast.success('Course retour creee');
        } catch (err) {
          toast.error('Course aller OK, mais erreur sur le retour: ' + (err.response?.data?.detail || 'Erreur'));
        }
      }

      setShowAddBooking(false);
      setNewBooking({ first_name: '', last_name: '', phone: '', email: '', pickup_address: '', destination_address: '', pickup_lat: null, pickup_lng: null, dest_lat: null, dest_lng: null, date: '', time: '', booking_date: '', booking_time: '', total_price: '', payment_method: 'cash', stops: [], notes: '', driver_id: '', driver_net_price: '', show_price_to_client: true, is_professional: false, company_name: '', company_address: '', company_nif: '', company_vat: '', flight_number: '', is_round_trip: false, return_date: '', return_time: '', return_price: '', return_driver_id: '', return_driver_net_price: '', return_flight_number: '' });
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la creation');
    }
  };

  const capturePayment = async () => {
    if (!paymentModal || !captureAmount) return;
    setPaymentLoading(true);
    try {
      const bid = paymentModal.booking.booking_id || paymentModal.booking.id;
      const res = await axios.post(`${API}/admin/quick-bookings/${bid}/capture?amount=${captureAmount}`, null, { headers: getAuthHeader() });
      toast.success(res.data.message);
      setPaymentModal(null);
      setCaptureAmount('');
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la capture');
    } finally { setPaymentLoading(false); }
  };

  const cancelPayment = async () => {
    if (!paymentModal || cancelFeePercent === null) return;
    setPaymentLoading(true);
    try {
      const bid = paymentModal.booking.booking_id || paymentModal.booking.id;
      const chargeFees = cancelFeePercent > 0;
      const res = await axios.post(
        `${API}/admin/quick-bookings/${bid}/cancel-payment?charge_fees=${chargeFees}&fee_percent=${cancelFeePercent}`,
        null,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setPaymentModal(null);
      setCancelFeePercent(null);
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally { setPaymentLoading(false); }
  };

  const sendDriverLink = async () => {
    if (!driverLinkModal || !driverLink) return;
    const bid = driverLinkModal.booking_id || driverLinkModal.id;
    setPaymentLoading(true);
    try {
      const res = await axios.post(
        `${API}/admin/quick-bookings/${bid}/send-driver-tracking?link=${encodeURIComponent(driverLink)}`,
        null,
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message || 'Lien envoye par WhatsApp');
      setDriverLinkModal(null);
      setDriverLink('');
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally { setPaymentLoading(false); }
  };

  const resendNotifications = async (bookingId) => {
    try {
      const res = await axios.post(
        `${API}/admin/quick-bookings/${bookingId}/resend-notifications`,
        null,
        { headers: getAuthHeader() }
      );
      const r = res.data.results || {};
      const parts = [];
      if (r.sms?.sid) parts.push('SMS');
      if (r.whatsapp?.sid) parts.push('WhatsApp');
      if (r.email?.success) parts.push('Email');
      toast.success(parts.length > 0 ? `Envoyé: ${parts.join(' + ')}` : 'Notifications renvoyées');
      fetchQuickBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors du renvoi');
    }
  };

  const fetchCalendarEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/calendar`, { headers: getAuthHeader() });
      setCalendarEvents(res.data);
    } catch (error) {
      if (error.response?.status === 401) handleAuthError();
    }
  }, [handleAuthError]);

  const fetchPrivilegedClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/privileged-clients`, { headers: getAuthHeader() });
      setPrivilegedClients(res.data);
    } catch (error) {
      if (error.response?.status === 401) handleAuthError();
    }
  }, [handleAuthError]);

  const fetchPrivilegeMap = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clients-privilege-bulk`, { headers: getAuthHeader() });
      // Build a phone-keyed map (try both raw & +33 normalised) for fast lookup
      const map = {};
      Object.entries(res.data.phones || {}).forEach(([ph, info]) => {
        const clean = (ph || '').replace(/\s+/g, '');
        map[clean] = info;
        // Also store alt forms (0X.. <-> +33X..)
        if (clean.startsWith('+33')) map['0' + clean.slice(3)] = info;
        else if (clean.startsWith('0')) map['+33' + clean.slice(1)] = info;
      });
      setPrivilegeMap(map);
    } catch (_) { /* silent */ }
  }, []);

  // Helper: returns privilege info {is_privileged, source, ride_count_6m} for a given phone (or null)
  const getPrivilegeInfo = useCallback((phone) => {
    if (!phone) return null;
    const clean = phone.replace(/\s+/g, '');
    return privilegeMap[clean] || privilegeMap['+33' + clean.replace(/^0/, '')] || privilegeMap['0' + clean.replace(/^\+33/, '')] || null;
  }, [privilegeMap]);

  const addPrivilegedClient = async () => {
    try {
      await axios.post(`${API}/admin/privileged-clients`, newPrivileged, { headers: getAuthHeader() });
      toast.success('Client privilege ajoute');
      setShowAddPrivileged(false);
      setNewPrivileged({ phone: '', first_name: '', last_name: '', fixed_price: '', route_description: '', pickup_keywords: '', dest_keywords: '', notes: '' });
      fetchPrivilegedClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deletePrivilegedClient = async (clientId, name) => {
    if (!window.confirm(`Supprimer ${name} des clients privileges ?`)) return;
    try {
      await axios.delete(`${API}/admin/privileged-clients/${clientId}`, { headers: getAuthHeader() });
      toast.success('Client supprime');
      fetchPrivilegedClients();
    } catch (e) { toast.error('Erreur'); }
  };

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/drivers`, { headers: getAuthHeader() });
      setDrivers(res.data);
    } catch (error) {
      if (error.response?.status === 401) handleAuthError();
    }
  }, [handleAuthError]);

  const saveDriver = async () => {
    try {
      if (editingDriver) {
        await axios.put(`${API}/admin/drivers/${editingDriver.id}`, newDriver, { headers: getAuthHeader() });
        toast.success('Chauffeur mis à jour');
      } else {
        await axios.post(`${API}/admin/drivers`, newDriver, { headers: getAuthHeader() });
        toast.success('Chauffeur ajouté');
      }
      setShowAddDriver(false);
      setEditingDriver(null);
      setNewDriver({ first_name: '', last_name: '', license_plate: '', phone: '', notes: '', commission_percent: 70, company_name: '', company_legal_form: '', company_siren: '', company_siret: '', company_ape: '', company_address: '', company_vat: '', evtc_card_number: '' });
      fetchDrivers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const editDriver = (d) => {
    setEditingDriver(d);
    setNewDriver({
      first_name: d.first_name || '',
      last_name: d.last_name || '',
      license_plate: d.license_plate || '',
      phone: d.phone || '',
      notes: d.notes || '',
      commission_percent: d.commission_percent ?? 70,
      company_name: d.company_name || '',
      company_legal_form: d.company_legal_form || '',
      company_siren: d.company_siren || '',
      company_siret: d.company_siret || '',
      company_ape: d.company_ape || '',
      company_address: d.company_address || '',
      company_vat: d.company_vat || '',
      evtc_card_number: d.evtc_card_number || '',
    });
    setShowAddDriver(true);
  };

  const deleteDriver = async (driverId, name) => {
    if (!window.confirm(`Supprimer le chauffeur ${name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/drivers/${driverId}`, { headers: getAuthHeader() });
      toast.success('Chauffeur supprimé');
      fetchDrivers();
    } catch (e) { toast.error('Erreur'); }
  };

  const regenerateDriverToken = async (driverId) => {
    if (!window.confirm('Régénérer le lien magique invalidera le lien actuel partagé au chauffeur. Continuer ?')) return;
    try {
      await axios.post(`${API}/admin/drivers/${driverId}/regenerate-token`, null, { headers: getAuthHeader() });
      toast.success('Nouveau lien généré');
      fetchDrivers();
    } catch (e) { toast.error('Erreur'); }
  };

  const copyDriverLink = (driver) => {
    const link = `${window.location.origin}/chauffeur/${driver.access_token}`;
    navigator.clipboard.writeText(link).then(
      () => toast.success('Lien copié !'),
      () => toast.error('Copie impossible')
    );
  };

  const assignDriverToBooking = async (bookingId, driverId) => {
    try {
      await axios.post(
        `${API}/admin/bookings/${bookingId}/assign-driver?driver_id=${encodeURIComponent(driverId || '')}`,
        null,
        { headers: getAuthHeader() }
      );
      toast.success(driverId ? 'Chauffeur assigné' : 'Chauffeur retiré');
      fetchQuickBookings();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };


  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeader(),
        params: { business_only: userFilter.businessOnly, limit: 50 }
      });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
      }
      console.error('Users error:', error);
    }
  }, [userFilter.businessOnly, handleAuthError]);

  const fetchBookings = useCallback(async () => {
    try {
      const params = { limit: 50 };
      if (bookingFilter.status) params.status = bookingFilter.status;
      if (bookingFilter.userId) params.user_id = bookingFilter.userId;
      
      const response = await axios.get(`${API}/admin/bookings`, {
        headers: getAuthHeader(),
        params
      });
      setBookings(response.data.bookings);
      setBookingsTotal(response.data.total);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
      }
      console.error('Bookings error:', error);
    }
  }, [bookingFilter.status, bookingFilter.userId, handleAuthError]);

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/invoices`, {
        headers: getAuthHeader(),
        params: { limit: 50 }
      });
      setInvoices(response.data.invoices);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleAuthError();
      }
      console.error('Invoices error:', error);
    }
  }, [handleAuthError]);

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}`, {
        headers: getAuthHeader()
      });
      setSelectedUser(response.data);
    } catch (error) {
      console.error('User details error:', error);
      toast.error('Erreur lors du chargement des détails');
    }
  };

  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Voulez-vous vraiment supprimer le compte de ${userName} ?\n\nCette action est irréversible et annulera toutes ses réservations en cours.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeader()
      });
      toast.success(`Utilisateur supprimé. ${response.data.bookings_cancelled} réservation(s) annulée(s).`);
      setSelectedUser(null);
      fetchUsers();
      fetchBookings();
      fetchStats();
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const adjustWallet = async (userId, amount, reason) => {
    if (!amount || amount === 0) {
      toast.error('Montant invalide');
      return;
    }
    setWalletLoading(true);
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/wallet`, 
        { amount: parseFloat(amount), reason },
        { headers: getAuthHeader() }
      );
      toast.success(res.data.message);
      setWalletAmount('');
      setWalletReason('Prime parrainage');
      // Refresh user details
      await fetchUserDetails(userId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setWalletLoading(false);
    }
  };

  const handleAdminCancelBooking = async (bookingId) => {
    if (!window.confirm('Voulez-vous vraiment annuler cette réservation ?')) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/bookings/${bookingId}/cancel`, null, {
        headers: getAuthHeader()
      });
      toast.success('Réservation annulée');
      fetchBookings();
      fetchStats();
    } catch (error) {
      console.error('Cancel booking error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    }
  };

  const handleAdminDeleteBooking = async (bookingId) => {
    if (!window.confirm('Voulez-vous vraiment SUPPRIMER cette réservation ?\n\nCette action est irréversible.')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: getAuthHeader()
      });
      toast.success('Réservation supprimée');
      fetchBookings();
      fetchStats();
    } catch (error) {
      console.error('Delete booking error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      const response = await axios.put(`${API}/admin/bookings/${bookingId}/status`, null, {
        headers: getAuthHeader(),
        params: { status }
      });
      toast.success('Statut mis à jour');
      
      // Update local state immediately for instant UI feedback
      setBookings(prev => prev.map(b => 
        b.id === bookingId 
          ? { ...b, status, payment_status: status === 'confirmed' ? 'paid' : b.payment_status }
          : b
      ));
      
      // Then refresh from server to get accurate data
      await fetchBookings();
      await fetchStats();
    } catch (error) {
      console.error('Update status error:', error);
      toast.error('Erreur lors de la mise à jour');
      // Refetch to restore correct state
      await fetchBookings();
    }
  };

  const generateMonthlyInvoices = async () => {
    setGeneratingInvoices(true);
    try {
      const response = await axios.post(`${API}/admin/invoices/generate-monthly`, null, {
        headers: getAuthHeader(),
        params: { year: invoiceYear, month: invoiceMonth }
      });
      toast.success(`${response.data.invoices_count} facture(s) générée(s)`);
      fetchInvoices();
    } catch (error) {
      console.error('Generate invoices error:', error);
      toast.error('Erreur lors de la génération des factures');
    } finally {
      setGeneratingInvoices(false);
    }
  };

  const exportBookings = async (format) => {
    try {
      const response = await axios.get(`${API}/admin/export/all`, {
        headers: getAuthHeader(),
        params: { format, year: invoiceYear, month: invoiceMonth },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `export-${invoiceYear}-${invoiceMonth}.${format === 'excel' ? 'xlsx' : 'csv'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export téléchargé');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_last_login');
    document.cookie = 'admin_token=; path=/; max-age=0; SameSite=Lax; Secure';
    navigate('/admin/login');
  };

  // Detect if running as standalone PWA (home screen) vs Safari browser
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  // Check push notification status on mount
  useEffect(() => {
    const checkPush = async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window && isStandalone) {
        try {
          const reg = await navigator.serviceWorker.ready;
          const sub = await reg.pushManager.getSubscription();
          setPushEnabled(!!sub);
        } catch { setPushEnabled(false); }
      }
    };
    checkPush();
  }, [isStandalone]);

  const enablePushNotifications = async () => {
    // iOS requires standalone mode (added to home screen) for push notifications
    if (!isStandalone) {
      toast.info(
        "Pour activer les notifications sur iPhone :\n1. Appuyez sur Partager (en bas)\n2. \"Sur l'écran d'accueil\"\n3. Ouvrez l'app depuis l'icône\n4. Puis cliquez Activer",
        { duration: 8000 }
      );
      return;
    }
    
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Veuillez autoriser les notifications dans les réglages de votre iPhone');
        setPushLoading(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      const padding = '='.repeat((4 - vapidKey.length % 4) % 4);
      const base64 = (vapidKey + padding).replace(/-/g, '+').replace(/_/g, '/');
      const rawData = window.atob(base64);
      const key = Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
      
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key
      });
      const subJson = sub.toJSON();
      const token = getAdminToken();
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subJson.endpoint,
        keys: subJson.keys
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setPushEnabled(true);
      toast.success('Notifications activées ! Vous recevrez une alerte sonore pour chaque nouvelle course.');
    } catch (err) {
      console.error('Push error:', err);
      toast.error('Erreur lors de l\'activation. Vérifiez les réglages de notifications de votre iPhone.');
    }
    setPushLoading(false);
  };

  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute('href', '/admin-manifest.json');
  }, []);

  useEffect(() => {
    const token = getAdminToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      // Validate admin token first (like Client AuthContext does)
      try {
        await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } });
      } catch (error) {
        if (error.response?.status === 401 || error.response?.status === 403) {
          // Token is confirmed invalid by server — redirect
          handleAuthError();
          return;
        }
        // Network error — token may still be valid, continue loading
        console.warn('Network error during admin validation, continuing...', error.message);
      }
      // Token validated or network error (keep session alive) — load all data
      await Promise.all([fetchStats(), fetchUsers(), fetchBookings(), fetchInvoices()]);
      setLoading(false);
    };
    
    loadData();
  }, [navigate, fetchStats, fetchUsers, fetchBookings, fetchInvoices, handleAuthError, getAdminToken]);

  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
    if (activeTab === 'bookings') { fetchBookings(); fetchQuickBookings(); fetchDrivers(); fetchPrivilegeMap(); }
    if (activeTab === 'users') { fetchPrivilegedClients(); fetchPrivilegeMap(); }
    if (activeTab === 'invoices') fetchInvoices();
    if (activeTab === 'calendar') fetchCalendarEvents();
    if (activeTab === 'drivers') fetchDrivers();
  }, [activeTab, fetchUsers, fetchBookings, fetchInvoices, fetchQuickBookings, fetchCalendarEvents, fetchDrivers, fetchPrivilegedClients, fetchPrivilegeMap]);

  const filteredUsers = users.filter(user => {
    if (!userFilter.search) return true;
    const search = userFilter.search.toLowerCase();
    return (
      user.email?.toLowerCase().includes(search) ||
      user.first_name?.toLowerCase().includes(search) ||
      user.last_name?.toLowerCase().includes(search) ||
      user.company_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="px-3 sm:px-6 py-3 sm:py-4 border-b border-zinc-800 bg-zinc-900/50 sticky top-0 z-50 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-youmi.png" alt="YOUMI" className="w-8 h-8 sm:w-12 sm:h-12" />
            <span className="text-emerald-500 font-bold text-sm sm:text-lg font-['Outfit']">Admin</span>
          </div>
          
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Session indicator - desktop only */}
            {(() => {
              const lastLogin = localStorage.getItem('admin_last_login');
              if (!lastLogin) return null;
              const d = new Date(lastLogin);
              const now = new Date();
              const diffH = Math.floor((now - d) / 3600000);
              const diffD = Math.floor(diffH / 24);
              const label = diffD > 0 ? `${diffD}j` : diffH > 0 ? `${diffH}h` : 'maintenant';
              return (
                <div className="hidden lg:flex items-center gap-1.5 text-xs text-zinc-500" data-testid="session-indicator">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Connecté · {label}
                </div>
              );
            })()}
            {/* Push Notifications - desktop only (banner handles mobile) */}
            <div className="hidden sm:block">
              <PushNotificationButton />
            </div>
            {/* Notifications */}
            <AdminNotifications adminToken={getAdminToken()} />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                fetchStats();
                fetchUsers();
                fetchBookings();
                fetchInvoices();
                toast.success('Données actualisées');
              }}
              className="text-zinc-400 hover:text-white p-1.5 sm:p-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-400 hover:text-red-400 p-1.5 sm:p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Push Notification Banner */}
        {!pushEnabled && (
          <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center justify-between gap-4" data-testid="push-banner">
            <div className="flex items-center gap-3">
              <BellOff className="w-6 h-6 text-amber-400 shrink-0" />
              <div>
                <p className="text-amber-200 font-semibold text-sm">Notifications désactivées</p>
                <p className="text-amber-200/70 text-xs">
                  {isStandalone
                    ? "Cliquez Activer pour recevoir une alerte sonore à chaque nouvelle course."
                    : "Ajoutez l'app à l'écran d'accueil d'abord (Partager → Sur l'écran d'accueil), puis activez les notifications."
                  }
                </p>
              </div>
            </div>
            <button
              onClick={enablePushNotifications}
              disabled={pushLoading}
              className="shrink-0 px-4 py-2 bg-amber-500 text-black font-bold rounded-lg hover:bg-amber-400 transition-colors text-sm disabled:opacity-50"
              data-testid="enable-push-btn"
            >
              {pushLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : isStandalone ? 'Activer' : 'Comment faire'}
            </button>
          </div>
        )}
        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'bookings', label: 'Réservations', icon: Car },
            { id: 'dashboard', label: 'Tableau de bord', icon: TrendingUp },
            { id: 'calendar', label: 'Calendrier', icon: CalendarDays },
            { id: 'users', label: 'Utilisateurs', icon: Users },
            { id: 'drivers', label: 'Chauffeurs', icon: IdCard },
            { id: 'availability', label: 'Disponibilités', icon: CalendarDays },
            { id: 'invoices', label: 'Factures', icon: FileText },
            { id: 'export', label: 'Exports', icon: Download },
            { id: 'security', label: 'Sécurité', icon: Shield },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-emerald-500 text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && stats && (
          <div className="space-y-8 animate-fade-in-up">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Utilisateurs</p>
                    <p className="text-2xl font-bold text-white font-['Outfit']">{stats.users.total}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-4 text-sm">
                  <span className="text-zinc-500">Pro: {stats.users.business}</span>
                  <span className="text-zinc-500">Part: {stats.users.individual}</span>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">Réservations</p>
                    <p className="text-2xl font-bold text-white font-['Outfit']">{stats.bookings.total}</p>
                  </div>
                </div>
                <div className="mt-4 flex gap-4 text-sm">
                  <span className="text-yellow-500">En attente: {stats.bookings.pending}</span>
                  <span className="text-emerald-500">Confirmées: {stats.bookings.confirmed}</span>
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Euro className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">CA Total TTC</p>
                    <p className="text-2xl font-bold text-white font-['Outfit']">{stats.revenue.total_ttc.toFixed(2)}€</p>
                  </div>
                </div>
                <div className="mt-4 text-sm text-zinc-500">
                  HT: {stats.revenue.total_ht.toFixed(2)}€
                </div>
              </div>

              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-zinc-400 text-sm">CA Mois TTC</p>
                    <p className="text-2xl font-bold text-white font-['Outfit']">{stats.revenue.month_ttc.toFixed(2)}€</p>
                  </div>
                </div>
                <div className="mt-4 text-sm text-zinc-500">
                  HT: {stats.revenue.month_ht.toFixed(2)}€
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="glass-card rounded-xl p-6">
              <h3 className="text-lg font-bold text-white font-['Outfit'] mb-4">Actions rapides</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => setActiveTab('users')}
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <Building2 className="w-5 h-5 text-emerald-500" />
                  <span className="text-white">Comptes Pro ({stats.users.business})</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 ml-auto" />
                </button>
                
                <button
                  onClick={() => {
                    setBookingFilter({ ...bookingFilter, status: 'pending' });
                    setActiveTab('bookings');
                  }}
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <Clock className="w-5 h-5 text-yellow-500" />
                  <span className="text-white">En attente ({stats.bookings.pending})</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 ml-auto" />
                </button>
                
                <button
                  onClick={() => setActiveTab('invoices')}
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <FileText className="w-5 h-5 text-blue-500" />
                  <span className="text-white">Factures</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 ml-auto" />
                </button>
                
                <button
                  onClick={() => setActiveTab('export')}
                  className="flex items-center gap-3 p-4 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                >
                  <Download className="w-5 h-5 text-purple-500" />
                  <span className="text-white">Exporter</span>
                  <ChevronRight className="w-4 h-4 text-zinc-500 ml-auto" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="space-y-4 animate-fade-in-up" data-testid="calendar-tab">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-white">Calendrier des courses</h2>
              <Button variant="outline" onClick={fetchCalendarEvents} className="border-zinc-700 text-zinc-300">
                <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
              </Button>
            </div>
            {/* Month Navigation */}
            <div className="flex items-center justify-center gap-4 mb-4">
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))} className="text-zinc-400 hover:text-white p-2">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h3 className="text-lg font-semibold text-white min-w-[200px] text-center">
                {calendarMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())}
              </h3>
              <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))} className="text-zinc-400 hover:text-white p-2">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
            {/* Calendar Grid */}
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="grid grid-cols-7 bg-zinc-800/50">
                {['Lun','Mar','Mer','Jeu','Ven','Sam','Dim'].map(d => (
                  <div key={d} className="p-2 text-center text-xs font-semibold text-zinc-400 border-b border-zinc-700">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {(() => {
                  const year = calendarMonth.getFullYear();
                  const month = calendarMonth.getMonth();
                  const firstDay = new Date(year, month, 1);
                  const lastDay = new Date(year, month + 1, 0);
                  const startPad = (firstDay.getDay() + 6) % 7;
                  const days = [];
                  for (let i = 0; i < startPad; i++) days.push(null);
                  for (let d = 1; d <= lastDay.getDate(); d++) days.push(d);
                  const today = new Date();
                  return days.map((day, idx) => {
                    if (!day) return <div key={`pad-${idx}`} className="min-h-[90px] border-b border-r border-zinc-800/50 bg-zinc-900/30" />;
                    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                    const dayEvents = calendarEvents.filter(e => e.date === dateStr);
                    const isToday = today.getFullYear() === year && today.getMonth() === month && today.getDate() === day;
                    return (
                      <div key={dateStr} className={`min-h-[90px] border-b border-r border-zinc-800/50 p-1 ${isToday ? 'bg-emerald-500/5' : ''}`}>
                        <div className={`text-xs font-medium mb-1 px-1 ${isToday ? 'text-emerald-400' : 'text-zinc-400'}`}>{day}</div>
                        <div className="space-y-0.5">
                          {dayEvents.slice(0, 3).map((ev, eidx) => (
                            <div key={eidx} className={`text-[10px] px-1 py-0.5 rounded truncate cursor-default ${
                              ev.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-300' :
                              ev.status === 'cancelled' || ev.status === 'rejected' ? 'bg-red-500/15 text-red-400' :
                              ev.status === 'completed' ? 'bg-blue-500/20 text-blue-300' :
                              'bg-yellow-500/20 text-yellow-300'
                            }`} title={`${ev.time} - ${ev.client}\n${ev.pickup} → ${ev.destination}${ev.price ? '\n'+ev.price+'€' : ''}`}>
                              <span className="font-medium">{ev.time}</span> {ev.client?.split(' ')[0]}
                              {ev.type === 'quick_booking' && <span className="ml-0.5 opacity-60">⚡</span>}
                            </div>
                          ))}
                          {dayEvents.length > 3 && <div className="text-[10px] text-zinc-500 px-1">+{dayEvents.length - 3} autres</div>}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap gap-4 text-xs text-zinc-400">
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-yellow-500/30" /> En attente</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-emerald-500/30" /> Confirmee</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-blue-500/30" /> Terminee</div>
              <div className="flex items-center gap-1"><div className="w-3 h-3 rounded bg-red-500/20" /> Annulee</div>
              <div className="flex items-center gap-1">⚡ Reservation rapide</div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="space-y-6 animate-fade-in-up">
            {/* Privileged Clients Section - AT THE TOP */}
            <div className="glass-card rounded-xl p-5 border border-amber-500/20">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-amber-400">Clients Privileges - Prix Fixes</h3>
                <Button onClick={() => setShowAddPrivileged(true)} className="bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="add-privileged-btn">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter
                </Button>
              </div>

              {showAddPrivileged && (
                <div className="bg-zinc-800 rounded-xl p-4 mb-4 border border-amber-500/30">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <Input placeholder="Prenom *" value={newPrivileged.first_name} onChange={e => setNewPrivileged({...newPrivileged, first_name: e.target.value})} className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="priv-firstname" />
                    <Input placeholder="Nom *" value={newPrivileged.last_name} onChange={e => setNewPrivileged({...newPrivileged, last_name: e.target.value})} className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="priv-lastname" />
                    <Input placeholder="Telephone *" value={newPrivileged.phone} onChange={e => setNewPrivileged({...newPrivileged, phone: e.target.value})} className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="priv-phone" />
                    <Input type="number" step="0.01" placeholder="Prix fixe EUR *" value={newPrivileged.fixed_price} onChange={e => setNewPrivileged({...newPrivileged, fixed_price: parseFloat(e.target.value) || ''})} className="bg-zinc-900 border-zinc-700 text-white text-sm" data-testid="priv-price" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <Input placeholder="Trajet (ex: Nice → Aeroport)" value={newPrivileged.route_description} onChange={e => setNewPrivileged({...newPrivileged, route_description: e.target.value})} className="bg-zinc-900 border-zinc-700 text-white text-sm" />
                    <Input placeholder="Notes" value={newPrivileged.notes} onChange={e => setNewPrivileged({...newPrivileged, notes: e.target.value})} className="bg-zinc-900 border-zinc-700 text-white text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <Input placeholder="Mots-cles depart (ex: nice,06100)" value={newPrivileged.pickup_keywords} onChange={e => setNewPrivileged({...newPrivileged, pickup_keywords: e.target.value})} className="bg-zinc-900 border-zinc-700 text-zinc-400 text-xs" />
                    <Input placeholder="Mots-cles destination (ex: aeroport,terminal)" value={newPrivileged.dest_keywords} onChange={e => setNewPrivileged({...newPrivileged, dest_keywords: e.target.value})} className="bg-zinc-900 border-zinc-700 text-zinc-400 text-xs" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={addPrivilegedClient} disabled={!newPrivileged.first_name || !newPrivileged.phone || !newPrivileged.fixed_price} className="bg-amber-600 hover:bg-amber-700 text-white text-sm" data-testid="save-privileged-btn">
                      <CheckCircle className="w-4 h-4 mr-1" /> Enregistrer
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddPrivileged(false)} className="border-zinc-700 text-zinc-400 text-sm">Annuler</Button>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {privilegedClients.length === 0 ? (
                  <p className="text-zinc-500 text-sm text-center py-3">Aucun client privilege. Cliquez sur "Ajouter" pour creer un tarif fixe.</p>
                ) : privilegedClients.map(pc => (
                  <div key={pc.id} className="bg-zinc-800/50 rounded-lg p-3 flex items-center justify-between border-l-4 border-l-amber-500">
                    <div>
                      <span className="text-white font-medium">{pc.first_name} {pc.last_name}</span>
                      <span className="text-zinc-500 text-xs ml-2">{pc.phone}</span>
                      {pc.route_description && <span className="text-zinc-400 text-xs ml-2">| {pc.route_description}</span>}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-amber-400 font-bold text-lg">{pc.fixed_price}EUR</span>
                      <Button size="sm" variant="outline" onClick={() => deletePrivilegedClient(pc.id, `${pc.first_name} ${pc.last_name}`)} className="border-red-500/50 text-red-400 text-xs h-7">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Rechercher..."
                  value={userFilter.search}
                  onChange={(e) => setUserFilter({ ...userFilter, search: e.target.value })}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                  data-testid="user-search"
                />
              </div>
              <Button
                variant={userFilter.businessOnly ? "default" : "outline"}
                onClick={() => setUserFilter({ ...userFilter, businessOnly: !userFilter.businessOnly })}
                className={userFilter.businessOnly ? "bg-emerald-500 text-black" : "border-zinc-700 text-zinc-300"}
                data-testid="filter-business"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Comptes Pro uniquement
              </Button>
            </div>

            {/* Users List */}
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-zinc-800/50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Entreprise</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Cagnotte</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {filteredUsers.map(user => (
                      <tr key={user.id} className="hover:bg-zinc-800/30">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center">
                              <span className="text-xs font-medium text-white">
                                {user.first_name?.[0]}{user.last_name?.[0]}
                              </span>
                            </div>
                            <span className="text-white font-medium">
                              {user.first_name} {user.last_name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-400">{user.email}</td>
                        <td className="px-4 py-3">
                          {user.is_business ? (
                            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs font-medium">
                              PRO
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs font-medium">
                              Particulier
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-zinc-400">{user.company_name || '-'}</td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${(user.referral_credits || 0) > 0 ? 'text-emerald-400' : 'text-zinc-500'}`}>
                            {(user.referral_credits || 0).toFixed(2)}€
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => fetchUserDetails(user.id)}
                            className="text-zinc-400 hover:text-white"
                            data-testid={`view-user-${user.id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length === 0 && (
                <div className="p-8 text-center text-zinc-500">
                  Aucun utilisateur trouvé
                </div>
              )}
            </div>

            {/* User Details Modal */}
            {selectedUser && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
                <div className="glass-card rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white font-['Outfit']">Détails client</h3>
                    <button onClick={() => setSelectedUser(null)} className="text-zinc-400 hover:text-white">
                      <XCircle className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Editable user info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-zinc-400 text-sm block mb-1">Prénom</label>
                      <Input
                        value={selectedUser.user.first_name || ''}
                        onChange={e => setSelectedUser({...selectedUser, user: {...selectedUser.user, first_name: e.target.value}})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="edit-user-firstname"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm block mb-1">Nom</label>
                      <Input
                        value={selectedUser.user.last_name || ''}
                        onChange={e => setSelectedUser({...selectedUser, user: {...selectedUser.user, last_name: e.target.value}})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="edit-user-lastname"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm block mb-1">Email</label>
                      <Input
                        value={selectedUser.user.email || ''}
                        onChange={e => setSelectedUser({...selectedUser, user: {...selectedUser.user, email: e.target.value}})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="edit-user-email"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm block mb-1">Téléphone</label>
                      <Input
                        value={selectedUser.user.phone || ''}
                        onChange={e => setSelectedUser({...selectedUser, user: {...selectedUser.user, phone: e.target.value}})}
                        className="bg-zinc-800 border-zinc-700 text-white"
                        data-testid="edit-user-phone"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm block mb-1">Code parrainage</label>
                      <p className="text-white font-mono py-2">{selectedUser.user.referral_code || '-'}</p>
                    </div>
                    <div className="flex items-end">
                      <Button
                        onClick={async () => {
                          try {
                            const res = await axios.put(`${API}/admin/users/${selectedUser.user.id}`, {
                              first_name: selectedUser.user.first_name,
                              last_name: selectedUser.user.last_name,
                              email: selectedUser.user.email,
                              phone: selectedUser.user.phone,
                            }, { headers: getAuthHeader() });
                            toast.success('Client mis à jour');
                            fetchUsers();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erreur de mise à jour');
                          }
                        }}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                        data-testid="save-user-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Enregistrer
                      </Button>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="mt-6 pt-6 border-t border-zinc-700">
                    <h4 className="text-base font-bold text-white mb-3">Statistiques</h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-500">{selectedUser.stats?.total_bookings || 0}</p>
                        <p className="text-zinc-400 text-xs">Courses</p>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-500">{selectedUser.stats?.completed_rides || 0}</p>
                        <p className="text-zinc-400 text-xs">Effectuées</p>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-500">{(selectedUser.stats?.total_spent_ttc || 0).toFixed(0)}€</p>
                        <p className="text-zinc-400 text-xs">Dépensé TTC</p>
                      </div>
                      <div className="bg-zinc-800 rounded-lg p-3 text-center">
                        <p className="text-xl font-bold text-emerald-500">{selectedUser.stats?.total_distance_km || 0} km</p>
                        <p className="text-zinc-400 text-xs">Distance</p>
                      </div>
                    </div>
                  </div>

                  {/* Loyalty */}
                  {selectedUser.loyalty && (
                    <div className="mt-4 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                      <h4 className="text-base font-bold text-white mb-2">Fidélité</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-zinc-400">6ème course (-20%) : </span>
                          {selectedUser.loyalty.completed_rides >= 6 ? (
                            <span className="text-emerald-400 font-medium">Déjà utilisé</span>
                          ) : selectedUser.loyalty.discount_eligible ? (
                            <span className="text-yellow-400 font-medium">Prochaine course !</span>
                          ) : (
                            <span className="text-white">encore {5 - selectedUser.loyalty.completed_rides} course(s)</span>
                          )}
                        </div>
                        <div>
                          <span className="text-zinc-400">10ème course (offerte) : </span>
                          {selectedUser.loyalty.completed_rides >= 10 ? (
                            <span className="text-emerald-400 font-medium">Déjà utilisé</span>
                          ) : selectedUser.loyalty.free_ride_eligible ? (
                            <span className="text-yellow-400 font-medium">Prochaine course !</span>
                          ) : (
                            <span className="text-white">encore {9 - selectedUser.loyalty.completed_rides} course(s)</span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Wallet / Cagnotte */}
                  <div className="mt-6 pt-6 border-t border-zinc-700">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-base font-bold text-white">Cagnotte</h4>
                      <span className="text-2xl font-bold text-emerald-500" data-testid="user-wallet-balance">
                        {(selectedUser.wallet?.balance || 0).toFixed(2)}€
                      </span>
                    </div>

                    {/* Adjust form */}
                    <div className="flex gap-2 mb-4">
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Montant (ex: 10 ou -5)"
                        value={walletAmount}
                        onChange={e => setWalletAmount(e.target.value)}
                        className="bg-zinc-800 border-zinc-700 text-white flex-1"
                        data-testid="wallet-amount-input"
                      />
                      <select
                        value={walletReason}
                        onChange={e => setWalletReason(e.target.value)}
                        className="px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                        data-testid="wallet-reason-select"
                      >
                        <option value="Prime parrainage">Prime parrainage</option>
                        <option value="Régularisation">Régularisation</option>
                        <option value="Geste commercial">Geste commercial</option>
                        <option value="Remboursement">Remboursement</option>
                        <option value="Correction">Correction</option>
                      </select>
                      <Button
                        onClick={() => adjustWallet(selectedUser.user.id, walletAmount, walletReason)}
                        disabled={walletLoading || !walletAmount}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="wallet-adjust-btn"
                      >
                        {walletLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Valider'}
                      </Button>
                    </div>

                    {/* Wallet History */}
                    {selectedUser.wallet?.history?.length > 0 && (
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        <p className="text-xs text-zinc-500 uppercase tracking-wide">Historique</p>
                        {selectedUser.wallet.history.map((h, i) => (
                          <div key={h.id || i} className="flex items-center justify-between py-1.5 px-3 bg-zinc-800/50 rounded-lg text-sm">
                            <div className="flex items-center gap-2">
                              <span className={h.amount > 0 ? 'text-emerald-400' : 'text-red-400'}>
                                {h.amount > 0 ? '+' : ''}{h.amount.toFixed(2)}€
                              </span>
                              <span className="text-zinc-400">{h.reason}</span>
                              {h.admin_action && <span className="text-xs px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded">Admin</span>}
                            </div>
                            <span className="text-zinc-600 text-xs">
                              {h.created_at ? new Date(h.created_at).toLocaleDateString('fr-FR') : '-'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Delete User Button */}
                  <div className="mt-6 pt-6 border-t border-zinc-700">
                    <Button
                      variant="outline"
                      onClick={() => deleteUser(selectedUser.user.id, `${selectedUser.user.first_name} ${selectedUser.user.last_name}`)}
                      className="w-full border-red-500/50 text-red-400 hover:bg-red-500/10"
                      data-testid="delete-user-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Supprimer ce compte
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6 animate-fade-in-up">
            {/* Header + Add button */}
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-white">Reservations</h2>
              <Button onClick={() => setShowAddBooking(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="add-booking-btn">
                <Plus className="w-4 h-4 mr-2" /> Ajouter une course
              </Button>
            </div>

            {/* Add Booking Modal */}
            {showAddBooking && (
              <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowAddBooking(false)}>
                <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-bold text-white">Ajouter une course</h3>
                    <button onClick={() => setShowAddBooking(false)} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Prenom *</label>
                        <Input value={newBooking.first_name} onChange={e => setNewBooking({...newBooking, first_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-firstname" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Nom *</label>
                        <Input value={newBooking.last_name} onChange={e => setNewBooking({...newBooking, last_name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-lastname" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Telephone *</label>
                        <Input value={newBooking.phone} onChange={e => setNewBooking({...newBooking, phone: e.target.value})} placeholder="06..." className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-phone" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Email</label>
                        <Input value={newBooking.email} onChange={e => setNewBooking({...newBooking, email: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-email" />
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">Adresse de depart *</label>
                      <GooglePlacesAutocomplete placeholder="Adresse de prise en charge" value={newBooking.pickup_address} onSelect={(p) => setNewBooking({...newBooking, pickup_address: p.display_name, pickup_lat: p.lat, pickup_lng: p.lng})} />
                    </div>

                    {/* Stops - Arrêts intermédiaires */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="text-zinc-400 text-xs">Arrets intermediaires</label>
                        <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400 text-xs h-7" onClick={() => {
                          const stops = newBooking.stops || [];
                          setNewBooking({...newBooking, stops: [...stops, {address: '', lat: null, lng: null, wait_minutes: 0}]});
                        }} data-testid="new-booking-add-stop-btn">
                          <Plus className="w-3 h-3 mr-1" /> Arret
                        </Button>
                      </div>
                      {(newBooking.stops || []).map((stop, idx) => (
                        <div key={idx} className="flex items-center gap-2 mb-2" data-testid={`new-booking-stop-row-${idx}`}>
                          <div className="flex-1">
                            <GooglePlacesAutocomplete
                              placeholder={`Arret ${idx + 1}`}
                              value={stop.address || ''}
                              onSelect={(place) => {
                                const newStops = [...(newBooking.stops || [])];
                                newStops[idx] = {...newStops[idx], address: place.display_name, lat: place.lat, lng: place.lng};
                                setNewBooking({...newBooking, stops: newStops});
                              }}
                            />
                          </div>
                          <div className="w-20">
                            <Input
                              type="number"
                              min="0"
                              placeholder="min"
                              value={stop.wait_minutes || ''}
                              onChange={e => {
                                const newStops = [...(newBooking.stops || [])];
                                newStops[idx] = {...newStops[idx], wait_minutes: parseInt(e.target.value) || 0};
                                setNewBooking({...newBooking, stops: newStops});
                              }}
                              className="bg-zinc-800 border-zinc-700 text-white text-xs"
                              title="Temps d'attente (min)"
                              data-testid={`new-booking-stop-wait-${idx}`}
                            />
                          </div>
                          <button onClick={() => {
                            const newStops = (newBooking.stops || []).filter((_, i) => i !== idx);
                            setNewBooking({...newBooking, stops: newStops});
                          }} className="text-red-400 hover:text-red-300 p-1" data-testid={`new-booking-stop-remove-${idx}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>

                    <div>
                      <label className="text-zinc-400 text-xs block mb-1">Destination *</label>
                      <GooglePlacesAutocomplete placeholder="Adresse de destination" value={newBooking.destination_address} onSelect={(p) => setNewBooking({...newBooking, destination_address: p.display_name, dest_lat: p.lat, dest_lng: p.lng})} />
                    </div>
                    <div className="grid grid-cols-2 gap-3 border-t border-zinc-800 pt-3">
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Date de la reservation</label>
                        <Input type="date" value={newBooking.booking_date} onChange={e => setNewBooking({...newBooking, booking_date: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-booking-date" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Heure de la reservation</label>
                        <Input type="time" value={newBooking.booking_time} onChange={e => setNewBooking({...newBooking, booking_time: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-booking-time" />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Date de prise en charge *</label>
                        <Input type="date" value={newBooking.date} onChange={e => setNewBooking({...newBooking, date: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-date" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Heure prise en charge *</label>
                        <Input type="time" value={newBooking.time} onChange={e => setNewBooking({...newBooking, time: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-time" />
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Prix (EUR) *</label>
                        <Input type="number" step="0.01" value={newBooking.total_price} onChange={e => setNewBooking({...newBooking, total_price: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="new-booking-price" />
                      </div>
                    </div>
                    {/* Show/hide price to client toggle (B2B use case) */}
                    <div className="p-3 bg-blue-500/5 border border-blue-500/30 rounded-lg">
                      <label className="flex items-start gap-2 cursor-pointer" data-testid="show-price-toggle-label">
                        <input
                          type="checkbox"
                          checked={newBooking.show_price_to_client !== false}
                          onChange={e => setNewBooking({ ...newBooking, show_price_to_client: e.target.checked })}
                          className="mt-1 w-4 h-4 accent-emerald-500"
                          data-testid="show-price-toggle"
                        />
                        <div>
                          <div className="text-blue-200 text-sm font-semibold flex items-center gap-1">
                            <Eye className="w-3 h-3" /> Afficher le prix au client
                          </div>
                          <div className="text-blue-400/70 text-xs mt-0.5">
                            {newBooking.show_price_to_client !== false
                              ? 'Le client verra le prix dans ses notifications (SMS, WhatsApp, Email, rappels).'
                              : 'Le prix sera masqué dans toutes les notifications client (utile B2B / facture séparée).'}
                          </div>
                        </div>
                      </label>
                    </div>
                    {/* B2B / Professional invoice toggle */}
                    <div className="p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg space-y-3">
                      <label className="flex items-start gap-2 cursor-pointer" data-testid="professional-toggle-label">
                        <input
                          type="checkbox"
                          checked={!!newBooking.is_professional}
                          onChange={e => setNewBooking({ ...newBooking, is_professional: e.target.checked })}
                          className="mt-1 w-4 h-4 accent-amber-500"
                          data-testid="professional-toggle"
                        />
                        <div>
                          <div className="text-amber-200 text-sm font-semibold flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Course professionnelle (facture entreprise)
                          </div>
                          <div className="text-amber-400/70 text-xs mt-0.5">
                            Émet la facture au nom d'une entreprise (NIF / N° TVA optionnels).
                          </div>
                        </div>
                      </label>
                      {newBooking.is_professional && (
                        <div className="space-y-2 pl-6">
                          <div>
                            <label className="text-amber-300 text-xs block mb-1">Nom de l'entreprise *</label>
                            <Input
                              value={newBooking.company_name}
                              onChange={e => setNewBooking({ ...newBooking, company_name: e.target.value })}
                              placeholder="Ex: ACME Sarl"
                              className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                              data-testid="company-name-input"
                            />
                          </div>
                          <div>
                            <label className="text-amber-300 text-xs block mb-1">Adresse complète *</label>
                            <textarea
                              value={newBooking.company_address}
                              onChange={e => setNewBooking({ ...newBooking, company_address: e.target.value })}
                              placeholder="Numéro, rue, code postal, ville, pays"
                              rows={2}
                              className="w-full px-3 py-2 bg-zinc-900 border border-amber-500/40 rounded-lg text-amber-100 text-sm resize-none"
                              data-testid="company-address-input"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-amber-300 text-xs block mb-1">NIF (optionnel)</label>
                              <Input
                                value={newBooking.company_nif}
                                onChange={e => setNewBooking({ ...newBooking, company_nif: e.target.value })}
                                placeholder="N° NIF"
                                className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                                data-testid="company-nif-input"
                              />
                            </div>
                            <div>
                              <label className="text-amber-300 text-xs block mb-1">N° TVA Intra (optionnel)</label>
                              <Input
                                value={newBooking.company_vat}
                                onChange={e => setNewBooking({ ...newBooking, company_vat: e.target.value })}
                                placeholder="Ex: FR12345678901"
                                className="bg-zinc-900 border-amber-500/40 text-amber-100 text-sm"
                                data-testid="company-vat-input"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Paiement</label>
                        <select value={newBooking.payment_method} onChange={e => setNewBooking({...newBooking, payment_method: e.target.value})} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm">
                          <option value="cash">Especes</option>
                          <option value="card_onboard">CB a bord</option>
                          <option value="stripe">CB en ligne</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-zinc-400 text-xs block mb-1">Notes</label>
                        <Input value={newBooking.notes} onChange={e => setNewBooking({...newBooking, notes: e.target.value})} placeholder="Notes internes..." className="bg-zinc-800 border-zinc-700 text-white text-sm" />
                      </div>
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1 flex items-center gap-1">
                        ✈️ N° de vol <span className="text-zinc-600">(optionnel — pour transfert aéroport)</span>
                      </label>
                      <Input
                        value={newBooking.flight_number}
                        onChange={e => setNewBooking({...newBooking, flight_number: e.target.value.toUpperCase().trim()})}
                        placeholder="Ex: AF1234, LH456, U24587"
                        className="bg-zinc-800 border-zinc-700 text-white text-sm font-mono"
                        data-testid="new-booking-flight-number"
                      />
                      {newBooking.flight_number && !isValidFlightFormat(newBooking.flight_number) ? (
                        <p className="text-amber-400 text-xs mt-1" data-testid="new-flight-warning">
                          ⚠️ Format inhabituel. Le suivi de vol risque de ne pas le trouver. Vérifie le code IATA (ex: AF1234).
                        </p>
                      ) : (
                        <p className="text-zinc-500 text-xs mt-1">Permet le suivi de vol en direct depuis le bon de réservation.</p>
                      )}
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs block mb-1 flex items-center gap-1">
                        <IdCard className="w-3 h-3" /> Chauffeur assigné
                      </label>
                      <select
                        value={newBooking.driver_id || ''}
                        onChange={e => {
                          const drvId = e.target.value;
                          const drv = drivers.find(d => d.id === drvId);
                          // Auto-compute net price from commission
                          let net = newBooking.driver_net_price;
                          if (drv && newBooking.total_price && (!newBooking.driver_net_price || true)) {
                            const total = parseFloat(newBooking.total_price);
                            const pct = typeof drv.commission_percent === 'number' ? drv.commission_percent : 70;
                            if (!isNaN(total) && total > 0) net = (total * pct / 100).toFixed(2);
                          }
                          if (!drvId) net = '';
                          setNewBooking({...newBooking, driver_id: drvId, driver_net_price: net});
                        }}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white text-sm"
                        data-testid="new-booking-driver"
                      >
                        <option value="">— Aucun (chauffeur par défaut sur le bon) —</option>
                        {drivers.map(d => (
                          <option key={d.id} value={d.id}>
                            {d.first_name} {d.last_name} — {d.license_plate}{typeof d.commission_percent === 'number' ? ` (${d.commission_percent}%)` : ''}
                          </option>
                        ))}
                      </select>
                      {drivers.length === 0 && (
                        <p className="text-amber-400/80 text-xs mt-1">
                          Aucun chauffeur dans la liste. Onglet <b>Chauffeurs</b> pour en ajouter.
                        </p>
                      )}
                    </div>
                    {newBooking.driver_id && (
                      <div className="p-3 bg-amber-500/5 border border-amber-500/30 rounded-lg">
                        <label className="text-amber-300 text-xs block mb-1 flex items-center gap-1 font-semibold">
                          <DollarSign className="w-3 h-3" /> Prix net chauffeur (EUR) — visible uniquement par vous et le chauffeur
                        </label>
                        <Input
                          type="number" step="0.01"
                          value={newBooking.driver_net_price}
                          onChange={e => setNewBooking({...newBooking, driver_net_price: e.target.value})}
                          placeholder="Auto-calculé selon commission %"
                          className="bg-zinc-900 border-amber-500/40 text-amber-200 text-sm"
                          data-testid="new-booking-driver-net-price"
                        />
                        <p className="text-amber-400/70 text-xs mt-1">
                          Le passager voit {newBooking.total_price || '?'}€ (TTC). Le chauffeur verra ce montant net sur son bon de réservation.
                        </p>
                      </div>
                    )}

                    {/* Aller-Retour (Round trip) toggle */}
                    <div className="p-3 bg-purple-500/5 border border-purple-500/30 rounded-lg">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="mt-1"
                          checked={!!newBooking.is_round_trip}
                          onChange={e => setNewBooking({ ...newBooking, is_round_trip: e.target.checked })}
                          data-testid="new-booking-round-trip-toggle"
                        />
                        <div className="flex-1">
                          <span className="text-purple-200 text-sm font-medium">Aller-Retour</span>
                          <p className="text-purple-300/70 text-xs mt-0.5">
                            Genere automatiquement 2 Bons de Commande (aller + retour avec adresses inversees).
                          </p>
                        </div>
                      </label>

                      {newBooking.is_round_trip && (
                        <div className="mt-3 space-y-3 pl-6 border-l-2 border-purple-500/30">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-purple-300 text-xs block mb-1">Date du retour *</label>
                              <Input
                                type="date"
                                value={newBooking.return_date}
                                onChange={e => setNewBooking({ ...newBooking, return_date: e.target.value })}
                                className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm"
                                data-testid="new-booking-return-date"
                              />
                            </div>
                            <div>
                              <label className="text-purple-300 text-xs block mb-1">Heure du retour *</label>
                              <Input
                                type="time"
                                value={newBooking.return_time}
                                onChange={e => setNewBooking({ ...newBooking, return_time: e.target.value })}
                                className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm"
                                data-testid="new-booking-return-time"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-purple-300 text-xs block mb-1">Prix du retour (EUR) *</label>
                            <Input
                              type="number" step="0.01"
                              value={newBooking.return_price}
                              onChange={e => setNewBooking({ ...newBooking, return_price: e.target.value })}
                              placeholder="Prix retour"
                              className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm"
                              data-testid="new-booking-return-price"
                            />
                          </div>
                          <div>
                            <label className="text-purple-300 text-xs block mb-1">Chauffeur du retour (optionnel)</label>
                            <select
                              value={newBooking.return_driver_id || ''}
                              onChange={e => {
                                const drvId = e.target.value;
                                const drv = drivers.find(d => d.id === drvId);
                                let net = '';
                                if (drv && newBooking.return_price) {
                                  const total = parseFloat(newBooking.return_price);
                                  const pct = typeof drv.commission_percent === 'number' ? drv.commission_percent : 70;
                                  if (!isNaN(total) && total > 0) net = (total * pct / 100).toFixed(2);
                                }
                                setNewBooking({ ...newBooking, return_driver_id: drvId, return_driver_net_price: net });
                              }}
                              className="w-full px-3 py-2 bg-zinc-900 border border-purple-500/40 rounded-lg text-purple-100 text-sm"
                              data-testid="new-booking-return-driver"
                            >
                              <option value="">— Meme chauffeur que l&apos;aller —</option>
                              {drivers.map(d => (
                                <option key={d.id} value={d.id}>
                                  {d.first_name} {d.last_name} — {d.license_plate}{typeof d.commission_percent === 'number' ? ` (${d.commission_percent}%)` : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          {newBooking.return_driver_id && (
                            <div>
                              <label className="text-purple-300 text-xs block mb-1">Prix net chauffeur retour (EUR)</label>
                              <Input
                                type="number" step="0.01"
                                value={newBooking.return_driver_net_price}
                                onChange={e => setNewBooking({ ...newBooking, return_driver_net_price: e.target.value })}
                                placeholder="Auto-calcule"
                                className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm"
                                data-testid="new-booking-return-driver-net-price"
                              />
                            </div>
                          )}
                          <div>
                            <label className="text-purple-300 text-xs block mb-1">N° de vol retour (optionnel)</label>
                            <Input
                              value={newBooking.return_flight_number}
                              onChange={e => setNewBooking({ ...newBooking, return_flight_number: e.target.value.toUpperCase().trim() })}
                              placeholder="Ex: AF1234"
                              className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm font-mono"
                              data-testid="new-booking-return-flight-number"
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    <Button onClick={createAdminBooking} disabled={!newBooking.first_name || !newBooking.phone || !newBooking.pickup_address || !newBooking.date || !newBooking.time || !newBooking.total_price || (newBooking.is_round_trip && (!newBooking.return_date || !newBooking.return_time || !newBooking.return_price))} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-3" data-testid="submit-new-booking">
                      <CheckCircle className="w-4 h-4 mr-2" /> {newBooking.is_round_trip ? 'Creer Aller + Retour' : 'Creer la course'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <select
                value={bookingFilter.status}
                onChange={(e) => setBookingFilter({ ...bookingFilter, status: e.target.value })}
                className="px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white"
                data-testid="booking-status-filter"
              >
                <option value="">Tous les statuts</option>
                <option value="upcoming">Courses a venir</option>
                <option value="pending">En attente</option>
                <option value="confirmed">Confirmees</option>
                <option value="completed">Terminees</option>
                <option value="cancelled">Annulees</option>
              </select>
              
              <span className="text-zinc-400 flex items-center">
                {(() => {
                  const qbNormalized = quickBookings.map(qb => ({
                    ...qb,
                    _isQuick: true,
                    _sortDate: qb.pickup_date || qb.date || '',
                    _sortTime: qb.pickup_time || qb.time || '',
                    status: qb.status || 'pending',
                    price: qb.total_price ? parseFloat(qb.total_price) : 0,
                  }));
                  const bkNormalized = bookings.map(b => ({ ...b, _isQuick: false, _sortDate: b.pickup_datetime?.split('T')[0] || '', _sortTime: b.pickup_datetime?.split('T')[1]?.slice(0,5) || '' }));
                  const all = [...bkNormalized, ...qbNormalized];
                  const today = new Date().toISOString().split('T')[0];
                  const filtered = bookingFilter.status === 'upcoming'
                    ? all.filter(b => b._sortDate >= today && b.status !== 'completed' && b.status !== 'cancelled' && b.status !== 'rejected')
                    : bookingFilter.status ? all.filter(b => b.status === bookingFilter.status) : all;
                  return `${filtered.length} course(s)`;
                })()}
              </span>
            </div>

            {/* Unified Bookings List */}
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-zinc-800/50">
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Client</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Trajet</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Prix</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Statut</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Paiement</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-zinc-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {(() => {
                      const qbNormalized = quickBookings.map(qb => ({
                        ...qb,
                        _isQuick: true,
                        _id: qb.booking_id || qb.id,
                        _sortDate: qb.pickup_date || qb.date || '',
                        _sortTime: qb.pickup_time || qb.time || '',
                        _clientName: `${qb.first_name || ''} ${qb.last_name || ''}`,
                        _pickup: qb.pickup_address || '',
                        _dest: qb.destination_address || '',
                        _price: qb.total_price ? parseFloat(qb.total_price) : 0,
                        status: qb.status || 'pending',
                      }));
                      const bkNormalized = bookings.map(b => ({
                        ...b,
                        _isQuick: false,
                        _id: b.id,
                        _sortDate: b.pickup_datetime?.split('T')[0] || '',
                        _sortTime: b.pickup_datetime?.split('T')[1]?.slice(0,5) || '',
                        _clientName: b.user_name || '',
                        _pickup: b.pickup_address || '',
                        _dest: b.destination_address || '',
                        _price: b.price || 0,
                        // Map classic booking fields to edit modal format
                        first_name: b.user_first_name || b.user_name?.split(' ')[0] || '',
                        last_name: b.user_last_name || b.user_name?.split(' ').slice(1).join(' ') || '',
                        phone: b.user_phone || '',
                        email: b.user_email || '',
                      }));
                      const all = [...bkNormalized, ...qbNormalized];
                      const today = new Date().toISOString().split('T')[0];
                      const isUpcoming = (b) => b._sortDate >= today && b.status !== 'completed' && b.status !== 'cancelled' && b.status !== 'rejected';
                      const filtered = bookingFilter.status === 'upcoming'
                        ? all.filter(isUpcoming)
                        : bookingFilter.status ? all.filter(b => b.status === bookingFilter.status) : all;
                      // Sort: upcoming rides FIRST (closest date ascending), then past rides (most recent descending)
                      const todayKey = today;
                      const sorted = filtered.sort((a, b) => {
                        const aKey = `${a._sortDate}${a._sortTime}`;
                        const bKey = `${b._sortDate}${b._sortTime}`;
                        const aUpcoming = a._sortDate >= todayKey && a.status !== 'completed' && a.status !== 'cancelled' && a.status !== 'rejected';
                        const bUpcoming = b._sortDate >= todayKey && b.status !== 'completed' && b.status !== 'cancelled' && b.status !== 'rejected';
                        // Upcoming rides always come before past rides
                        if (aUpcoming && !bUpcoming) return -1;
                        if (!aUpcoming && bUpcoming) return 1;
                        // Both upcoming: closest date first (ascending)
                        if (aUpcoming && bUpcoming) return aKey.localeCompare(bKey);
                        // Both past: most recent first (descending)
                        return bKey.localeCompare(aKey);
                      });
                      
                      if (sorted.length === 0) return (
                        <tr><td colSpan="8" className="p-8 text-center text-zinc-500">Aucune course trouvee</td></tr>
                      );

                      const todayStr = new Date().toISOString().split('T')[0];
                      return sorted.map(item => {
                        const isUpcomingItem = item._sortDate >= todayStr && item.status !== 'completed' && item.status !== 'cancelled' && item.status !== 'rejected';
                        return (
                        <tr key={`${item._isQuick ? 'q' : 'b'}-${item._id}`} className={isUpcomingItem ? 'bg-emerald-500/5 hover:bg-emerald-500/10 border-l-2 border-l-emerald-500' : 'hover:bg-zinc-800/30'}>
                          <td className="px-4 py-3">
                            {item._isQuick ? (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-orange-500/20 text-orange-400">Rapide</span>
                            ) : (
                              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">Réservée</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-white text-sm">{item._sortDate ? new Date(item._sortDate).toLocaleDateString('fr-FR') : '-'}</div>
                            <div className="text-zinc-500 text-xs">{item._sortTime || ''}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="text-white text-sm">{item._clientName}</span>
                              {(() => {
                                const p = getPrivilegeInfo(item.phone);
                                if (!p?.is_privileged) return null;
                                const tooltip = p.source === 'admin_list'
                                  ? 'Client privilège (liste admin) — prix fixes garantis'
                                  : `Client privilège — ${p.ride_count_6m} courses sur 6 mois`;
                                return (
                                  <span
                                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-400/15 text-amber-300 border border-amber-400/40"
                                    title={tooltip}
                                    data-testid={`privilege-badge-${item._id}`}
                                  >
                                    <Award className="w-2.5 h-2.5" /> Privilège
                                  </span>
                                );
                              })()}
                            </div>
                            {item._isQuick && item.phone && <div className="text-zinc-500 text-xs">{item.phone}</div>}
                            {!item._isQuick && item.company_name && <div className="text-emerald-400 text-xs">{item.company_name}</div>}
                            {item.client_location_link && (
                              <a href={item.client_location_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-red-400 hover:underline flex items-center gap-0.5">
                                <MapPin className="w-3 h-3" /> Position client
                              </a>
                            )}
                            {item.driver_tracking_link && (
                              <a href={item.driver_tracking_link} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-400 hover:underline flex items-center gap-0.5">
                                <Navigation className="w-3 h-3" /> Suivi chauffeur
                              </a>
                            )}
                            {/* Driver assignment quick selector */}
                            <div className="mt-1 flex items-center gap-1" data-testid={`driver-assign-${item._id}`}>
                              <IdCard className="w-3 h-3 text-zinc-500 shrink-0" />
                              {item.driver_first_name ? (
                                <select
                                  value={item.driver_id || ''}
                                  onChange={e => assignDriverToBooking(item._id, e.target.value)}
                                  className="bg-zinc-800 border border-emerald-500/30 rounded text-emerald-300 text-[10px] py-0.5 px-1 max-w-[140px]"
                                  title={`${item.driver_first_name} ${item.driver_last_name} • ${item.driver_license_plate}`}
                                >
                                  <option value="">— Retirer —</option>
                                  {drivers.map(d => (
                                    <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.license_plate})</option>
                                  ))}
                                </select>
                              ) : (
                                drivers.length > 0 ? (
                                  <select
                                    value=""
                                    onChange={e => e.target.value && assignDriverToBooking(item._id, e.target.value)}
                                    className="bg-zinc-800 border border-zinc-700 rounded text-zinc-400 text-[10px] py-0.5 px-1 max-w-[140px]"
                                  >
                                    <option value="">Assigner chauffeur</option>
                                    {drivers.map(d => (
                                      <option key={d.id} value={d.id}>{d.first_name} {d.last_name} ({d.license_plate})</option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-zinc-600 text-[10px] italic">Aucun chauffeur</span>
                                )
                              )}
                            </div>
                            {item.client_ip && (
                              <div className="mt-1 flex items-center gap-1" data-testid={`booking-ip-${item._id}`}>
                                <Shield className="w-3 h-3 text-zinc-500 shrink-0" />
                                <code className="text-[10px] text-zinc-500 font-mono">{item.client_ip}</code>
                                <button
                                  onClick={async () => {
                                    if (!window.confirm(`Bloquer l'IP ${item.client_ip} ?\n\nToutes les requêtes provenant de cette IP recevront HTTP 403.`)) return;
                                    try {
                                      await axios.post(`${API}/admin/blocked-ips`, { ip: item.client_ip, reason: `Bloqué depuis réservation ${item._id?.slice(0,8)}` }, { headers: getAuthHeader() });
                                      toast.success(`IP ${item.client_ip} bloquée`);
                                    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
                                  }}
                                  className="text-[10px] text-red-400 hover:text-red-300 underline"
                                  data-testid={`block-ip-${item._id}`}
                                  title="Bloquer cette IP"
                                >
                                  Bloquer
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-white text-sm truncate max-w-[200px]">{item._pickup?.split(',')[0]}</div>
                            <div className="text-zinc-500 text-xs truncate max-w-[200px]">→ {item._dest?.split(',')[0]}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-emerald-500 font-bold">{item._price ? `${parseFloat(item._price).toFixed(2)}€` : '-'}</div>
                            {!item._isQuick && item.distance_km && <div className="text-zinc-500 text-xs">{item.distance_km?.toFixed(1)} km</div>}
                            {item.driver_id && (
                              <div className="mt-1 flex items-center gap-1" title="Prix net chauffeur (visible uniquement par vous et lui)">
                                <span className="text-amber-400 text-[10px] font-semibold">Net:</span>
                                <input
                                  type="number" step="0.01"
                                  defaultValue={item.driver_net_price || ''}
                                  placeholder="—"
                                  onBlur={async (e) => {
                                    const v = e.target.value.trim();
                                    if (!v || v === (item.driver_net_price || '')) return;
                                    try {
                                      await axios.put(
                                        `${API}/admin/bookings/${item._id}/driver-net-price?net_price=${encodeURIComponent(v)}`,
                                        null,
                                        { headers: getAuthHeader() }
                                      );
                                      toast.success(`Prix net chauffeur : ${parseFloat(v).toFixed(2)}€`);
                                      fetchQuickBookings();
                                    } catch (err) { toast.error('Erreur'); }
                                  }}
                                  className="w-16 bg-zinc-900 border border-amber-500/30 rounded text-amber-300 text-[10px] px-1 py-0.5"
                                  data-testid={`driver-net-input-${item._id}`}
                                />
                                <span className="text-amber-400 text-[10px]">€</span>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' :
                              item.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                              item.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {item.status === 'confirmed' ? 'Confirmée' :
                               item.status === 'pending' ? 'En attente' :
                               item.status === 'completed' ? 'Terminée' : 'Annulée'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {item._isQuick ? (
                              <div className="flex flex-col gap-1">
                                <span className="text-xs text-zinc-400">{
                                  item.payment_method === 'stripe' ? 'CB' :
                                  item.payment_method === 'cash' ? 'Espèces' :
                                  item.payment_method === 'card_onboard' ? 'CB bord' :
                                  item.payment_method || '-'
                                }</span>
                                {item.payment_status === 'authorized' && <span className="text-xs text-amber-400">{item.amount_authorized}€ bloqué</span>}
                                {item.payment_status === 'captured' && <span className="text-xs text-emerald-400">Capturé</span>}
                                {item.notifications && (
                                  <span className="text-[10px] flex items-center gap-0.5">
                                    {item.notifications.client_channel === 'email' && <span className="text-emerald-400">Email</span>}
                                    {item.notifications.client_channel === 'whatsapp' && <span className="text-emerald-400">WA</span>}
                                    {item.notifications.client_channel === 'sms' && <span className="text-emerald-400">SMS</span>}
                                    {!item.notifications.client_channel && (
                                      <>
                                        {item.notifications.client_whatsapp?.sid ? <span className="text-emerald-400">WA</span> : null}
                                        {item.notifications.client_sms?.sid ? <span className="text-emerald-400">SMS</span> : null}
                                        {item.notifications.client_email?.sent ? <span className="text-emerald-400">Email</span> : null}
                                      </>
                                    )}
                                    {item.notifications.admin_call?.sid && <span className="text-blue-400">Appel</span>}
                                  </span>
                                )}
                              </div>
                            ) : (
                              item.payment_status === 'paid' ? (
                                <span className="text-emerald-400 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Payé</span>
                              ) : (
                                <span className="text-zinc-500">En attente</span>
                              )
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              {item._isQuick ? (
                                <>
                                  {item.status === 'pending' && (
                                    <>
                                      <Button size="sm" onClick={() => updateQuickBookingStatus(item._id, 'confirmed')} className="bg-emerald-500 text-black text-xs px-2 py-1 h-auto" title="Confirmer la réservation" data-testid={`confirm-unified-${item._id}`}>
                                        <CheckCircle className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => updateQuickBookingStatus(item._id, 'cancelled')} className="border-red-500/50 text-red-400 text-xs px-2 py-1 h-auto" title="Annuler la réservation">
                                        <XCircle className="w-3 h-3" />
                                      </Button>
                                    </>
                                  )}
                                  {item.payment_status === 'authorized' && (
                                    <>
                                      <Button size="sm" onClick={() => { setPaymentModal({ type: 'capture', booking: item }); setCaptureAmount(item.amount_authorized || item.total_price || ''); }} className="bg-emerald-600 text-white text-xs px-2 py-1 h-auto" title={item.payment_intent_id ? "Capturer le paiement (encaisser)" : "Capturer le paiement — payment_intent_id absent, peut échouer"} data-testid={`capture-unified-${item._id}`}>
                                        <DollarSign className="w-3 h-3" />
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => { setPaymentModal({ type: 'cancel', booking: item }); setCancelFeePercent(null); }} className="border-red-500/50 text-red-400 text-xs px-2 py-1 h-auto" title="Annuler le paiement / Libérer la caution">
                                        <Ban className="w-3 h-3" />
                                      </Button>
                                    </>
                                  )}
                                  {(item.status === 'confirmed' || item.payment_status === 'authorized') && (
                                    <Button size="sm" variant="outline" onClick={() => setDriverLinkModal(item)} className="border-blue-500/50 text-blue-400 text-xs px-2 py-1 h-auto" title="Lien GPS chauffeur (à envoyer au chauffeur)" data-testid={`driver-link-unified-${item._id}`}>
                                      <Navigation className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => resendNotifications(item._id)} className="border-emerald-500/50 text-emerald-400 text-xs px-2 py-1 h-auto" title="Renvoyer SMS/WhatsApp/Email au client" data-testid={`resend-unified-${item._id}`}>
                                    <MessageCircle className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" onClick={() => setRideModal(item)} className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2 py-1 h-auto font-semibold flex items-center gap-1" title="Gérer la course : En route → Arrivé → Démarré → Terminé" data-testid={`ride-unified-${item._id}`}>
                                    <Car className="w-3 h-3" /> Étapes
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={async () => { try { const r = await axios.get(`${API}/admin/bookings/${item._id}/bon-de-commande`, { headers: getAuthHeader(), responseType: 'blob' }); const u = URL.createObjectURL(new Blob([r.data], {type:'application/pdf'})); const a = document.createElement('a'); a.href = u; a.download = `bon-commande-${item._id.slice(0,8)}.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u); toast.success('Bon de commande telecharge'); } catch(e) { toast.error('Erreur bon de commande'); }}} className="border-amber-500/50 text-amber-400 text-xs px-2 py-1 h-auto" title="Bon de commande" data-testid={`bon-commande-${item._id}`}>
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => setEditingQuickBooking({...item, date: item.pickup_date || item.date, time: item.pickup_time || item.time})} className="border-zinc-600 text-zinc-300 text-xs px-2 py-1 h-auto" title="Modifier la réservation">
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => deleteQuickBooking(item._id, item._clientName)} className="border-red-500/50 text-red-400 text-xs px-2 py-1 h-auto" title="Supprimer la réservation">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {item.whatsapp_link && (
                                    <a href={item.whatsapp_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-[#25D366] text-white text-xs px-2 py-1 rounded">
                                      <MessageCircle className="w-3 h-3" />
                                    </a>
                                  )}
                                  {item.status === 'pending' && (
                                    <Button size="sm" onClick={() => updateBookingStatus(item.id, 'confirmed')} className="bg-emerald-500 text-black text-xs px-2 py-1 h-auto">
                                      <CheckCircle className="w-3 h-3" />
                                    </Button>
                                  )}
                                  {item.status === 'confirmed' && (
                                    <Button size="sm" onClick={() => updateBookingStatus(item.id, 'completed')} className="bg-blue-500 text-white text-xs px-2 py-1 h-auto">
                                      <CheckCircle className="w-3 h-3" />
                                    </Button>
                                  )}
                                  {item.status !== 'completed' && item.status !== 'cancelled' && (
                                    <Button size="sm" variant="outline" onClick={() => handleAdminCancelBooking(item.id)} className="border-yellow-500/50 text-yellow-400 text-xs px-2 py-1 h-auto">
                                      <XCircle className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="outline" onClick={() => setEditingQuickBooking({...item, date: item._sortDate, time: item._sortTime, pickup_address: item._pickup, destination_address: item._dest, total_price: String(item._price || '')})} className="border-zinc-600 text-zinc-300 text-xs px-2 py-1 h-auto">
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={async () => { try { const r = await axios.get(`${API}/admin/bookings/${item._id}/bon-de-commande`, { headers: getAuthHeader(), responseType: 'blob' }); const u = URL.createObjectURL(new Blob([r.data], {type:'application/pdf'})); const a = document.createElement('a'); a.href = u; a.download = `bon-commande-${item._id.slice(0,8)}.pdf`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(u); toast.success('Bon de commande telecharge'); } catch(e) { toast.error('Erreur bon de commande'); }}} className="border-amber-500/50 text-amber-400 text-xs px-2 py-1 h-auto" title="Bon de commande">
                                    <FileText className="w-3 h-3" />
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => handleAdminDeleteBooking(item.id)} className="border-red-500/50 text-red-400 text-xs px-2 py-1 h-auto">
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );});
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Availability Tab */}
        {activeTab === 'availability' && (
          <div className="animate-fade-in-up">
            <AvailabilityManager adminToken={getAdminToken()} />
          </div>
        )}

        {/* Drivers Tab */}
        {activeTab === 'drivers' && (
          <DriversTab
            drivers={drivers}
            showAddDriver={showAddDriver} setShowAddDriver={setShowAddDriver}
            newDriver={newDriver} setNewDriver={setNewDriver}
            editingDriver={editingDriver} setEditingDriver={setEditingDriver}
            saveDriver={saveDriver} editDriver={editDriver} deleteDriver={deleteDriver}
            copyDriverLink={copyDriverLink} regenerateDriverToken={regenerateDriverToken}
          />
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <InvoicesTab
            invoices={invoices}
            invoiceMonth={invoiceMonth} setInvoiceMonth={setInvoiceMonth}
            invoiceYear={invoiceYear} setInvoiceYear={setInvoiceYear}
            generateMonthlyInvoices={generateMonthlyInvoices}
            generatingInvoices={generatingInvoices}
            getAuthHeader={getAuthHeader}
            refreshInvoices={fetchInvoices}
          />
        )}

        {/* Export Tab */}
        {activeTab === 'export' && (
          <ExportTab
            invoiceMonth={invoiceMonth} setInvoiceMonth={setInvoiceMonth}
            invoiceYear={invoiceYear} setInvoiceYear={setInvoiceYear}
            exportBookings={exportBookings}
          />
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <SecurityTab getAuthHeader={getAuthHeader} />
        )}

        {/* Payment Modal (Capture / Cancel) */}
        <PaymentModal
          paymentModal={paymentModal} setPaymentModal={setPaymentModal}
          captureAmount={captureAmount} setCaptureAmount={setCaptureAmount}
          cancelFeePercent={cancelFeePercent} setCancelFeePercent={setCancelFeePercent}
          paymentLoading={paymentLoading}
          capturePayment={capturePayment} cancelPayment={cancelPayment}
        />

        {/* Edit Quick Booking Modal */}
        {editingQuickBooking && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => { setEditingQuickBooking(null); setReturnForm(null); }}>
            <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-white">Modifier la reservation</h3>
                <button onClick={() => { setEditingQuickBooking(null); setReturnForm(null); }} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                {/* Client info */}
                <div className="grid grid-cols-2 gap-3">
                  {[{key:'first_name',label:'Prenom'},{key:'last_name',label:'Nom'},{key:'phone',label:'Telephone'},{key:'email',label:'Email'}].map(f => (
                    <div key={f.key}>
                      <label className="text-zinc-400 text-xs block mb-1">{f.label}</label>
                      <Input value={editingQuickBooking[f.key]||''} onChange={e => setEditingQuickBooking({...editingQuickBooking, [f.key]: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid={`edit-qb-${f.key}`} />
                    </div>
                  ))}
                </div>

                {/* Addresses with Google Places */}
                <div className="border-t border-zinc-800 pt-4">
                  <label className="text-zinc-400 text-xs block mb-1">Adresse de depart</label>
                  <GooglePlacesAutocomplete
                    placeholder="Adresse de prise en charge"
                    value={editingQuickBooking.pickup_address || ''}
                    onSelect={(place) => setEditingQuickBooking({...editingQuickBooking, pickup_address: place.display_name, pickup_lat: place.lat, pickup_lng: place.lng})}
                  />
                </div>

                {/* Stops */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-zinc-400 text-xs">Arrets intermediaires</label>
                    <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400 text-xs h-7" onClick={() => {
                      const stops = editingQuickBooking.stops || [];
                      setEditingQuickBooking({...editingQuickBooking, stops: [...stops, {address: '', lat: null, lng: null, wait_minutes: 0}]});
                    }} data-testid="add-stop-btn">
                      <Plus className="w-3 h-3 mr-1" /> Arret
                    </Button>
                  </div>
                  {(editingQuickBooking.stops || []).map((stop, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <div className="flex-1">
                        <GooglePlacesAutocomplete
                          placeholder={`Arret ${idx + 1}`}
                          value={stop.address || ''}
                          onSelect={(place) => {
                            const newStops = [...(editingQuickBooking.stops || [])];
                            newStops[idx] = {...newStops[idx], address: place.display_name, lat: place.lat, lng: place.lng};
                            setEditingQuickBooking({...editingQuickBooking, stops: newStops});
                          }}
                        />
                      </div>
                      <div className="w-20">
                        <Input type="number" min="0" placeholder="min" value={stop.wait_minutes || ''} onChange={e => {
                          const newStops = [...(editingQuickBooking.stops || [])];
                          newStops[idx] = {...newStops[idx], wait_minutes: parseInt(e.target.value) || 0};
                          setEditingQuickBooking({...editingQuickBooking, stops: newStops});
                        }} className="bg-zinc-800 border-zinc-700 text-white text-xs" title="Temps d'attente (min)" />
                      </div>
                      <button onClick={() => {
                        const newStops = (editingQuickBooking.stops || []).filter((_, i) => i !== idx);
                        setEditingQuickBooking({...editingQuickBooking, stops: newStops});
                      }} className="text-red-400 hover:text-red-300 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                <div>
                  <label className="text-zinc-400 text-xs block mb-1">Destination</label>
                  <GooglePlacesAutocomplete
                    placeholder="Adresse de destination"
                    value={editingQuickBooking.destination_address || ''}
                    onSelect={(place) => setEditingQuickBooking({...editingQuickBooking, destination_address: place.display_name, dest_lat: place.lat, dest_lng: place.lng})}
                  />
                </div>

                {/* Date/Time + Price */}
                <div className="grid grid-cols-3 gap-3 border-t border-zinc-800 pt-4">
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Date</label>
                    <Input type="date" value={editingQuickBooking.date||''} onChange={e => setEditingQuickBooking({...editingQuickBooking, date: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="edit-qb-date" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Heure</label>
                    <Input type="time" value={editingQuickBooking.time||''} onChange={e => setEditingQuickBooking({...editingQuickBooking, time: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white text-sm" data-testid="edit-qb-time" />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-xs block mb-1">Prix (€)</label>
                    <Input type="number" step="0.01" value={editingQuickBooking.total_price||''} onChange={e => setEditingQuickBooking({...editingQuickBooking, total_price: e.target.value, manual_price: true})} className="bg-zinc-800 border-zinc-700 text-white text-sm font-bold" data-testid="edit-qb-total_price" />
                  </div>
                </div>

                {editingQuickBooking.distance_km && (
                  <p className="text-zinc-500 text-xs">{editingQuickBooking.distance_km}km - {Math.round(editingQuickBooking.duration_min || 0)}min</p>
                )}

                {/* Flight number */}
                <div className="border-t border-zinc-800 pt-4">
                  <label className="text-zinc-400 text-xs block mb-1 flex items-center gap-1">
                    ✈️ N° de vol <span className="text-zinc-600">(optionnel — transfert aéroport)</span>
                  </label>
                  <Input
                    value={editingQuickBooking.flight_number || ''}
                    onChange={e => setEditingQuickBooking({...editingQuickBooking, flight_number: e.target.value.toUpperCase()})}
                    placeholder="Ex: AF1234, LH456, U24587"
                    className="bg-zinc-800 border-zinc-700 text-white text-sm font-mono"
                    data-testid="edit-qb-flight-number"
                  />
                  {editingQuickBooking.flight_number && !isValidFlightFormat(editingQuickBooking.flight_number) ? (
                    <p className="text-amber-400 text-xs mt-1" data-testid="edit-flight-warning">
                      ⚠️ Format inhabituel. Le suivi de vol risque de ne pas le trouver. Vérifie le code IATA (ex: AF1234).
                    </p>
                  ) : (
                    <p className="text-zinc-500 text-xs mt-1">Permet le suivi de vol en direct depuis le bon de réservation.</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 border-t border-zinc-800 pt-4">
                  <Button onClick={recalculatePrice} variant="outline" className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10" data-testid="recalculate-btn">
                    <Calculator className="w-4 h-4 mr-2" /> Recalculer le prix
                  </Button>
                  <Button onClick={saveQuickBookingEdit} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-qb-edit-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Enregistrer
                  </Button>
                </div>

                {/* Aller-Retour: create a return booking from this one */}
                <div className="border-t border-zinc-800 pt-4">
                  {!returnForm ? (
                    <Button onClick={() => setReturnForm({ date: '', time: '', price: '', driver_id: editingQuickBooking.driver_id || '', driver_net_price: '', flight_number: '' })} variant="outline" className="w-full border-purple-500/50 text-purple-300 hover:bg-purple-500/10" data-testid="create-return-btn">
                      <RotateCcw className="w-4 h-4 mr-2" /> Creer une course retour (adresses inversees)
                    </Button>
                  ) : (
                    <div className="p-3 bg-purple-500/5 border border-purple-500/30 rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-purple-200 text-sm font-semibold">Course retour</h4>
                        <button onClick={() => setReturnForm(null)} className="text-purple-400 hover:text-purple-200 text-xs">Annuler</button>
                      </div>
                      <p className="text-purple-300/70 text-xs">
                        Depart: <b>{editingQuickBooking.destination_address}</b><br />
                        Destination: <b>{editingQuickBooking.pickup_address}</b>
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <Input type="date" value={returnForm.date} onChange={e => setReturnForm({ ...returnForm, date: e.target.value })} className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm" data-testid="return-form-date" />
                        <Input type="time" value={returnForm.time} onChange={e => setReturnForm({ ...returnForm, time: e.target.value })} className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm" data-testid="return-form-time" />
                      </div>
                      <Input type="number" step="0.01" placeholder="Prix retour (EUR)" value={returnForm.price} onChange={e => setReturnForm({ ...returnForm, price: e.target.value })} className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm" data-testid="return-form-price" />
                      <select
                        value={returnForm.driver_id || ''}
                        onChange={e => {
                          const drvId = e.target.value;
                          const drv = drivers.find(d => d.id === drvId);
                          let net = '';
                          if (drv && returnForm.price) {
                            const total = parseFloat(returnForm.price);
                            const pct = typeof drv.commission_percent === 'number' ? drv.commission_percent : 70;
                            if (!isNaN(total) && total > 0) net = (total * pct / 100).toFixed(2);
                          }
                          setReturnForm({ ...returnForm, driver_id: drvId, driver_net_price: net });
                        }}
                        className="w-full px-3 py-2 bg-zinc-900 border border-purple-500/40 rounded-lg text-purple-100 text-sm"
                        data-testid="return-form-driver"
                      >
                        <option value="">— Meme chauffeur que l&apos;aller —</option>
                        {drivers.map(d => (
                          <option key={d.id} value={d.id}>{d.first_name} {d.last_name} — {d.license_plate}</option>
                        ))}
                      </select>
                      <Input placeholder="N° de vol retour (optionnel)" value={returnForm.flight_number} onChange={e => setReturnForm({ ...returnForm, flight_number: e.target.value.toUpperCase().trim() })} className="bg-zinc-900 border-purple-500/40 text-purple-100 text-sm font-mono" data-testid="return-form-flight" />
                      <Button
                        onClick={async () => {
                          if (!returnForm.date || !returnForm.time || !returnForm.price) {
                            toast.error('Date, heure et prix du retour requis');
                            return;
                          }
                          try {
                            const payload = {
                              first_name: editingQuickBooking.first_name || '',
                              last_name: editingQuickBooking.last_name || '',
                              phone: editingQuickBooking.phone || '',
                              email: editingQuickBooking.email || '',
                              pickup_address: editingQuickBooking.destination_address,
                              destination_address: editingQuickBooking.pickup_address,
                              pickup_lat: editingQuickBooking.dest_lat,
                              pickup_lng: editingQuickBooking.dest_lng,
                              dest_lat: editingQuickBooking.pickup_lat,
                              dest_lng: editingQuickBooking.pickup_lng,
                              date: returnForm.date,
                              time: returnForm.time,
                              total_price: returnForm.price,
                              payment_method: editingQuickBooking.payment_method || 'cash',
                              stops: [],
                              notes: (editingQuickBooking.notes ? editingQuickBooking.notes + ' | ' : '') + 'Retour de la course aller',
                              driver_id: returnForm.driver_id || editingQuickBooking.driver_id || '',
                              driver_net_price: returnForm.driver_net_price || '',
                              show_price_to_client: editingQuickBooking.show_price_to_client !== false,
                              is_professional: !!editingQuickBooking.is_professional,
                              company_name: editingQuickBooking.company_name || '',
                              company_address: editingQuickBooking.company_address || '',
                              company_nif: editingQuickBooking.company_nif || '',
                              company_vat: editingQuickBooking.company_vat || '',
                              flight_number: returnForm.flight_number || '',
                            };
                            await axios.post(`${API}/admin/create-booking`, payload, { headers: getAuthHeader() });
                            toast.success('Course retour creee');
                            setReturnForm(null);
                            setEditingQuickBooking(null);
                            fetchQuickBookings();
                          } catch (e) {
                            toast.error(e.response?.data?.detail || 'Erreur lors de la creation du retour');
                          }
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        data-testid="return-form-submit"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" /> Creer la course retour
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Ride Workflow Modal */}
        <RideWorkflowModal
          rideModal={rideModal} setRideModal={setRideModal}
          rideLoading={rideLoading}
          advanceRideStep={advanceRideStep}
        />

        {/* Driver Tracking Link Modal */}
        <DriverLinkModal
          driverLinkModal={driverLinkModal} setDriverLinkModal={setDriverLinkModal}
          driverLink={driverLink} setDriverLink={setDriverLink}
          paymentLoading={paymentLoading}
          sendDriverLink={sendDriverLink}
        />
      </div>
    </div>
  );
};
